import {
  BarChart3,
  ChevronRight,
  ClipboardCheck,
  Cog,
  ExternalLink,
  EyeOff,
  Mail,
  MapPin,
  Pencil,
  Phone,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getCustomer } from '../api/customers';
import {
  deactivatePool,
  deletePool,
  getRecentPoolServices,
  reactivatePool,
  subscribeToPool,
} from '../api/pools';
import AppButton from '../components/common/AppButton';
import AppLayout from '../components/layout/AppLayout';
import PoolIcon from '../components/layout/PoolIcon';
import DeactivatePoolModal from '../components/pools/DeactivatePoolModal';
import DeletePoolModal from '../components/pools/DeletePoolModal';
import PoolActionsMenu from '../components/pools/PoolActionsMenu';
import PoolBillingBadge from '../components/pools/PoolBillingBadge';
import PoolStatusBadge from '../components/pools/PoolStatusBadge';
import ReactivatePoolModal from '../components/pools/ReactivatePoolModal';
import { useAuth } from '../context/AuthContext';
import type { Customer } from '../types/customer';
import type { DeactivationReason, Pool, PoolService } from '../types/pool';
import {
  DeactivationReasonDisplay,
  PoolStyleDisplay,
  PoolTypeDisplay,
} from '../types/pool';

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const shortDateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
});

const formatDaysAgo = (date: Date): string => {
  const msPerDay = 1000 * 60 * 60 * 24;
  const diffDays = Math.round((Date.now() - date.getTime()) / msPerDay);

  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return '1 day ago';
  return `${diffDays} days ago`;
};

const PoolDetailsPage = () => {
  const { poolId } = useParams<{ poolId: string }>();
  const { company, hasPermission } = useAuth();
  const navigate = useNavigate();

  const [pool, setPool] = useState<Pool | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [services, setServices] = useState<PoolService[] | null>(null);
  const [servicesError, setServicesError] = useState<string | null>(null);
  const [customer, setCustomer] = useState<Customer | null | undefined>(
    undefined
  );
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const canViewCustomers = hasPermission('canViewCustomers');

  useEffect(() => {
    if (!company?.id || !poolId) return;

    setPool(undefined);
    setError(null);

    const unsubscribe = subscribeToPool(
      company.id,
      poolId,
      (result) => setPool(result),
      () => setError('Failed to load this pool. Please try again.')
    );

    return unsubscribe;
  }, [company?.id, poolId]);

  useEffect(() => {
    if (!company?.id || !poolId) return;

    setServices(null);
    setServicesError(null);

    getRecentPoolServices(company.id, poolId, 5)
      .then(setServices)
      .catch(() => setServicesError('Failed to load service history.'));
  }, [company?.id, poolId]);

  useEffect(() => {
    // customers/{customerId} is admin-only per firestore.rules - skip the
    // fetch entirely for non-admins rather than let it fail permission-denied.
    if (!company?.id || !pool?.customer.id || !canViewCustomers) return;

    setCustomer(undefined);
    getCustomer(company.id, pool.customer.id)
      .then(setCustomer)
      .catch(() => setCustomer(null));
  }, [company?.id, pool?.customer.id, canViewCustomers]);

  const loading = pool === undefined;
  const notFound = pool === null;
  const canEditPools = hasPermission('canEditPools');
  const canDeletePools = hasPermission('canDeletePools');

  const handleConfirmDeactivate = async (
    reason: DeactivationReason,
    reasonText?: string
  ) => {
    if (!company?.id || !pool) return;
    await deactivatePool(company.id, pool.id, reason, reasonText);
    setShowDeactivateModal(false);
  };

  const handleConfirmReactivate = async () => {
    if (!company?.id || !pool) return;
    await reactivatePool(company.id, pool.id);
    setShowReactivateModal(false);
  };

  const handleConfirmDelete = async () => {
    if (!company?.id || !pool) return;
    setIsDeleting(true);
    try {
      await deletePool(company.id, pool.id);
    } catch (err) {
      setIsDeleting(false);
      throw err;
    }
    setShowDeleteModal(false);
    toast.success('Pool deleted successfully.');
    navigate('/pools');
  };

  // Check error first: subscribeToPool's error callback can fire without ever
  // calling the success callback (e.g. immediate permission-denied), which
  // would otherwise leave `pool` as `undefined` (loading=true) forever.
  if (error) {
    return (
      <AppLayout title="Pools">
        <div className="bg-white border border-grey3 rounded-[20px] px-8 py-16 text-center shadow-[0_6px_22px_rgba(0,29,47,0.05)]">
          <h3 className="text-xl font-extrabold text-dark mb-2">
            Something went wrong
          </h3>
          <p className="text-sm text-light max-w-[380px] mx-auto mb-5">
            {error}
          </p>
          <AppButton onClick={() => navigate('/pools')}>
            Back to Pools
          </AppButton>
        </div>
      </AppLayout>
    );
  }

  if (loading || (notFound && isDeleting)) {
    return (
      <AppLayout title="Pools">
        <PoolDetailsSkeleton />
      </AppLayout>
    );
  }

  if (notFound) {
    return (
      <AppLayout title="Pools">
        <div className="bg-white border border-grey3 rounded-[20px] px-8 py-16 text-center shadow-[0_6px_22px_rgba(0,29,47,0.05)]">
          <h3 className="text-xl font-extrabold text-dark mb-2">
            Pool not found
          </h3>
          <p className="text-sm text-light max-w-[380px] mx-auto mb-5">
            This pool may have been deleted, or you don't have access to it.
          </p>
          <AppButton onClick={() => navigate('/pools')}>
            Back to Pools
          </AppButton>
        </div>
      </AppLayout>
    );
  }

  const isActive = pool.isActive !== false;

  return (
    <>
      <AppLayout
        headerContent={
          <div>
            <div className="flex items-center gap-1.5 text-[13px] text-light font-medium mb-1.5">
              <Link to="/pools" className="hover:text-primary">
                Pools
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-grey10" />
              <span className="text-dark font-semibold">{pool.poolName}</span>
            </div>
            <div className="flex items-center gap-3">
              <h1 className="text-[26px] font-extrabold text-dark tracking-tight">
                {pool.poolName}
              </h1>
              <PoolStatusBadge isActive={isActive} />
            </div>
          </div>
        }
        actions={
          <>
            {canEditPools && (
              <AppButton
                variant="secondary"
                leftIcon={<Pencil />}
                onClick={() => navigate(`/pools/${pool.id}/edit`)}
              >
                Edit Pool
              </AppButton>
            )}
            {/* Record Service flow isn't implemented in web yet; button only reflects the disabled-when-inactive state. */}
            <AppButton
              leftIcon={<ClipboardCheck />}
              disabled={!isActive}
              title={
                !isActive
                  ? 'Reactivate the pool to record a service'
                  : undefined
              }
            >
              Record Service
            </AppButton>
            {canEditPools && (
              <PoolActionsMenu
                isActive={isActive}
                canDelete={canDeletePools}
                onSetInactive={() => setShowDeactivateModal(true)}
                onSetActive={() => setShowReactivateModal(true)}
                onDelete={() => setShowDeleteModal(true)}
              />
            )}
          </>
        }
      >
        {!isActive && (
          <div className="mb-5 flex items-start gap-3 rounded-xl border border-warning/25 bg-warning/[0.09] px-4 py-3">
            <EyeOff className="mt-0.5 h-5 w-5 flex-shrink-0 text-warning" />
            <div className="text-[13.5px] leading-relaxed text-[#8a4a24]">
              This pool is inactive. It can&rsquo;t take new services or
              repairs, and it&rsquo;s excluded from financial reports. All past
              data is kept - reactivate any time.
              {pool.deactivationReason && (
                <div className="mt-0.5 font-bold text-[#7a3f1e]">
                  Reason:{' '}
                  {pool.deactivationReasonText ||
                    DeactivationReasonDisplay[pool.deactivationReason]}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-6 items-start">
          {/* Left column */}
          <div className="flex flex-col gap-6 min-w-0">
            {/* Pool Information */}
            <Card
              icon={<PoolIcon className="w-[18px] h-[18px] text-primary" />}
              title="Pool Information"
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-xl bg-cloud text-primary flex items-center justify-center flex-shrink-0">
                  <PoolIcon className="w-[30px] h-[30px]" />
                </div>
                <div>
                  <div className="text-lg font-semibold text-dark">
                    {pool.poolName}
                  </div>
                  <div className="text-sm text-light mt-0.5">
                    {PoolStyleDisplay[pool.poolStyle] ?? pool.poolStyle}
                    {' • '}
                    {PoolTypeDisplay[pool.poolType] ?? pool.poolType} water
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-x-7 gap-y-4">
                <InfoPair
                  className="col-span-2"
                  label={
                    <>
                      <MapPin className="w-[13px] h-[13px]" /> Address
                    </>
                  }
                  value={
                    <>
                      {pool.poolStreetAddress}
                      <br />
                      {pool.poolCity}, {pool.poolState}
                    </>
                  }
                />
                <InfoPair
                  label="Pool Style"
                  value={PoolStyleDisplay[pool.poolStyle] ?? pool.poolStyle}
                />
                <InfoPair
                  label="Volume"
                  value={pool.poolVolume ? `${pool.poolVolume} gallons` : '-'}
                />
                <InfoPair label="Size" value={pool.poolSize || '-'} />
                <InfoPair
                  label="Sanitation"
                  value={PoolTypeDisplay[pool.poolType] ?? pool.poolType}
                />
                <InfoPair
                  className="col-span-2"
                  label="Billing Model"
                  value={
                    <PoolBillingBadge
                      billingModelType={pool.billingModel.type}
                    />
                  }
                />
                {!isActive && pool.deactivationReason && (
                  <InfoPair
                    className="col-span-2"
                    label={
                      <>
                        <Cog className="w-[13px] h-[13px]" /> Deactivation
                        Reason
                      </>
                    }
                    value={
                      <span className="text-light font-medium text-sm">
                        {pool.deactivationReasonText || pool.deactivationReason}
                      </span>
                    }
                  />
                )}
              </div>
            </Card>

            {/* Water Chemistry Readings */}
            <Card title="Water Chemistry Readings" noPadding>
              {servicesError ? (
                <div className="px-6 py-10 text-center text-sm text-light">
                  {servicesError}
                </div>
              ) : services === null ? (
                <div className="px-6 py-10 text-center text-sm text-light">
                  Loading readings…
                </div>
              ) : services.length === 0 ? (
                <div className="px-6 py-10 text-center text-sm text-light">
                  No water chemistry readings recorded yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>
                        {[
                          'Date',
                          'pH',
                          'Chlorine (ppm)',
                          'Alkalinity (ppm)',
                          'Technician',
                        ].map((h) => (
                          <th
                            key={h}
                            className="text-left text-[11px] font-bold uppercase tracking-wider text-grey10 px-5 py-3 bg-lighter border-b border-grey3 whitespace-nowrap"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {services.map((service) => {
                        const testResults = service.testResults ?? {};
                        const phValue = testResults['phValue'];
                        const freeChlorine = testResults['freeChlorine'];
                        const totalAlkalinity = testResults['totalAlkalinity'];

                        return (
                          <tr
                            key={service.id}
                            className="border-b border-grey3 last:border-b-0 hover:bg-cloud transition-colors"
                          >
                            <td className="px-5 py-3.5 text-sm whitespace-nowrap">
                              <span className="font-semibold text-dark">
                                {dateFormatter.format(service.serviceDate)}
                              </span>
                            </td>
                            <td className="px-5 py-3.5 text-sm font-semibold text-dark whitespace-nowrap">
                              {phValue ?? '-'}
                            </td>
                            <td className="px-5 py-3.5 text-sm font-semibold text-dark whitespace-nowrap">
                              {freeChlorine ?? '-'}
                            </td>
                            <td className="px-5 py-3.5 text-sm font-semibold text-dark whitespace-nowrap">
                              {totalAlkalinity ?? '-'}
                            </td>
                            <td className="px-5 py-3.5 text-sm text-light whitespace-nowrap">
                              {service.servicedBy?.name ?? '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* Service History */}
            <Card title="Service History" noPadding>
              {servicesError ? (
                <div className="px-6 py-10 text-center text-sm text-light">
                  {servicesError}
                </div>
              ) : services === null ? (
                <div className="px-6 py-10 text-center text-sm text-light">
                  Loading service history…
                </div>
              ) : services.length === 0 ? (
                <div className="px-6 py-10 text-center text-sm text-light">
                  No service visits recorded yet.
                </div>
              ) : (
                <div className="flex flex-col">
                  {services.map((service) => (
                    <div
                      key={service.id}
                      className="flex gap-3.5 px-5 py-4 border-b border-grey3 last:border-b-0 hover:bg-cloud transition-colors"
                    >
                      <div className="w-10 h-10 rounded-[11px] bg-cloud text-primary flex items-center justify-center flex-shrink-0">
                        <ClipboardCheck className="w-[18px] h-[18px]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2.5 flex-wrap mb-0.5">
                          <span className="text-sm font-bold text-dark">
                            {dateFormatter.format(service.serviceDate)}
                          </span>
                        </div>
                        <div className="text-xs text-grey10 mb-1.5">
                          {service.servicedBy?.name ?? 'Unknown technician'}
                        </div>
                        {service.serviceNotes && (
                          <div className="text-[13px] text-light leading-relaxed">
                            {service.serviceNotes}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Right column */}
          <div className="flex flex-col gap-6 min-w-0">
            {/* Customer */}
            <Card
              title="Customer"
              action={
                <Link
                  to={`/customers/${pool.customer.id}`}
                  className="text-[13px] font-semibold text-primary inline-flex items-center gap-1"
                >
                  Profile <ExternalLink className="w-3.5 h-3.5" />
                </Link>
              }
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-[46px] h-[46px] rounded-[13px] bg-primary text-white text-base font-bold flex items-center justify-center flex-shrink-0">
                  {pool.customer.name
                    .split(/\s+/)
                    .map((p) => p[0])
                    .slice(0, 2)
                    .join('')
                    .toUpperCase()}
                </div>
                <div>
                  <div className="text-[15px] font-bold text-dark">
                    {pool.customer.name}
                  </div>
                </div>
              </div>

              {!canViewCustomers ? (
                <div className="flex flex-col gap-2.5 text-sm text-light">
                  <span className="inline-flex items-center gap-1">
                    <Mail className="w-3.5 h-3.5" /> Contact details on customer
                    profile
                  </span>
                </div>
              ) : customer === undefined ? (
                <div className="flex flex-col gap-2.5">
                  {[0, 1].map((i) => (
                    <div
                      key={i}
                      className="h-4 w-40 rounded bg-gradient-to-r from-grey4 via-lighter to-grey4 bg-[length:400%_100%] animate-pulse"
                    />
                  ))}
                </div>
              ) : customer === null ? (
                <div className="flex flex-col gap-2.5 text-sm text-light">
                  <span className="inline-flex items-center gap-1">
                    <Mail className="w-3.5 h-3.5" /> Contact details on customer
                    profile
                  </span>
                </div>
              ) : (
                <div className="flex flex-col gap-2.5 text-sm text-dark font-medium">
                  {customer.email && (
                    <span className="inline-flex items-center gap-2.5">
                      <Mail className="w-4 h-4 text-primary flex-shrink-0" />
                      {customer.email}
                    </span>
                  )}
                  {customer.phone && (
                    <span className="inline-flex items-center gap-2.5">
                      <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                      {customer.phone}
                    </span>
                  )}
                  {!customer.email && !customer.phone && (
                    <span className="text-light">No contact info on file.</span>
                  )}
                </div>
              )}
            </Card>

            {/* Quick Stats */}
            <Card
              icon={<BarChart3 className="w-[18px] h-[18px] text-primary" />}
              title="Quick Stats"
            >
              {services === null ? (
                <div className="grid grid-cols-2 gap-3.5">
                  {[0, 1].map((i) => (
                    <div
                      key={i}
                      className="bg-lighter border border-grey3 rounded-xl p-3.5"
                    >
                      <div className="h-3 w-20 rounded bg-gradient-to-r from-grey4 via-lighter to-grey4 bg-[length:400%_100%] animate-pulse mb-3" />
                      <div className="h-6 w-14 rounded bg-gradient-to-r from-grey4 via-lighter to-grey4 bg-[length:400%_100%] animate-pulse" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3.5">
                  <StatBox
                    label="Recent Services"
                    value={services.length}
                    sub="Most recent, up to 5"
                  />
                  <StatBox
                    label="Last Service"
                    value={
                      services.length > 0
                        ? shortDateFormatter.format(services[0].serviceDate)
                        : '-'
                    }
                    sub={
                      services.length > 0
                        ? formatDaysAgo(services[0].serviceDate)
                        : undefined
                    }
                  />
                </div>
              )}
            </Card>
          </div>
        </div>
      </AppLayout>

      <DeactivatePoolModal
        open={showDeactivateModal}
        poolName={pool.poolName}
        onClose={() => setShowDeactivateModal(false)}
        onConfirm={handleConfirmDeactivate}
      />
      <ReactivatePoolModal
        open={showReactivateModal}
        poolName={pool.poolName}
        onClose={() => setShowReactivateModal(false)}
        onConfirm={handleConfirmReactivate}
      />
      <DeletePoolModal
        open={showDeleteModal}
        poolName={pool.poolName}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

interface CardProps {
  icon?: React.ReactNode;
  title: string;
  action?: React.ReactNode;
  /** Set when the child content manages its own padding (e.g. tables, lists). */
  noPadding?: boolean;
  children: React.ReactNode;
}

const Card = ({ icon, title, action, noPadding, children }: CardProps) => (
  <div className="bg-white border border-grey3 rounded-[20px] shadow-[0_6px_22px_rgba(0,29,47,0.05)] overflow-hidden">
    <div className="flex items-center justify-between px-[22px] py-[18px] border-b border-grey3">
      <h3 className="text-base font-bold text-dark flex items-center gap-2">
        {icon}
        {title}
      </h3>
      {action}
    </div>
    {noPadding ? children : <div className="p-[22px]">{children}</div>}
  </div>
);

interface InfoPairProps {
  label: React.ReactNode;
  value: React.ReactNode;
  className?: string;
}

const InfoPair = ({ label, value, className }: InfoPairProps) => (
  <div className={className}>
    <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-grey10 mb-1.5">
      {label}
    </div>
    <div className="text-[15px] font-semibold text-dark leading-snug">
      {value}
    </div>
  </div>
);

interface StatBoxProps {
  label: string;
  value: React.ReactNode;
  sub?: string;
}

const StatBox = ({ label, value, sub }: StatBoxProps) => (
  <div className="bg-lighter border border-grey3 rounded-xl p-3.5">
    <div className="text-[11px] font-bold uppercase tracking-wider text-grey10 mb-1.5">
      {label}
    </div>
    <div className="text-2xl font-extrabold text-dark leading-none">
      {value}
    </div>
    {sub && <div className="text-xs text-light mt-1.5">{sub}</div>}
  </div>
);

const PoolDetailsSkeleton = () => (
  <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-6">
    <div className="flex flex-col gap-6">
      {[0, 1].map((i) => (
        <div
          key={i}
          className="bg-white border border-grey3 rounded-[20px] p-6 shadow-[0_6px_22px_rgba(0,29,47,0.05)]"
        >
          <div className="h-5 w-40 rounded bg-gradient-to-r from-grey4 via-lighter to-grey4 bg-[length:400%_100%] animate-pulse mb-4" />
          <div className="h-24 w-full rounded bg-gradient-to-r from-grey4 via-lighter to-grey4 bg-[length:400%_100%] animate-pulse" />
        </div>
      ))}
    </div>
    <div className="flex flex-col gap-6">
      {[0, 1].map((i) => (
        <div
          key={i}
          className="bg-white border border-grey3 rounded-[20px] p-6 shadow-[0_6px_22px_rgba(0,29,47,0.05)]"
        >
          <div className="h-5 w-28 rounded bg-gradient-to-r from-grey4 via-lighter to-grey4 bg-[length:400%_100%] animate-pulse mb-4" />
          <div className="h-16 w-full rounded bg-gradient-to-r from-grey4 via-lighter to-grey4 bg-[length:400%_100%] animate-pulse" />
        </div>
      ))}
    </div>
  </div>
);

export default PoolDetailsPage;
